import React, { useMemo } from 'react';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    Legend
} from 'recharts';
import { format, parseISO, startOfDay, subDays, isAfter } from 'date-fns';
import { ru } from 'date-fns/locale';

const ReportCharts = ({ data, dateFrom, dateTo, section = 'all' }) => {
    // Цвета (совпадают с DashboardHome)
    const COLORS = {
        primary: '#667eea',
        secondary: '#764ba2',
        google: '#4285F4',
        yandex: '#FC3F1D',
        success: '#10b981',
        warning: '#f59e0b',
        danger: '#ef4444',
        info: '#3b82f6'
    };

    const BRAND_SCORE_COLORS = [
        '#10b981', // Отлично
        '#3b82f6', // Хорошо
        '#f59e0b', // Средне
        '#f97316', // Плохо
        '#ef4444'  // Критично
    ];

    // Подготовка данных для графиков
    const chartData = useMemo(() => {
        if (!data || data.length === 0) return { trends: [], distribution: [] };

        // Группировка по дням
        const daysMap = new Map();

        // Определяем диапазон дат
        let startDate = dateFrom ? new Date(dateFrom) : subDays(new Date(), 30);
        let endDate = dateTo ? new Date(dateTo) : new Date();

        // Если диапазон не выбран, берем мин/макс из данных или последние 30 дней
        if (!dateFrom && data.length > 0) {
            const dates = data.map(d => new Date(d.check_date));
            const minDate = new Date(Math.min(...dates));
            if (isAfter(startDate, minDate)) startDate = minDate;
        }

        // Заполняем карту дней нулями
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const dateKey = format(d, 'yyyy-MM-dd');
            daysMap.set(dateKey, {
                date: dateKey,
                checksCount: 0,
                googleChecks: 0,
                yandexChecks: 0,
                totalScore: 0,
                scoreCount: 0,
                googleScoreTotal: 0,
                googleScoreCount: 0,
                yandexScoreTotal: 0,
                yandexScoreCount: 0
            });
        }

        // Агрегируем данные
        data.forEach(item => {
            const dateKey = format(new Date(item.check_date), 'yyyy-MM-dd');
            if (daysMap.has(dateKey)) {
                const day = daysMap.get(dateKey);
                day.checksCount++;
                if (item.search_engine === 'google') day.googleChecks++;
                if (item.search_engine === 'yandex') day.yandexChecks++;

                if (item.brand_score !== null) {
                    day.totalScore += item.brand_score;
                    day.scoreCount++;

                    if (item.search_engine === 'google') {
                        day.googleScoreTotal += item.brand_score;
                        day.googleScoreCount++;
                    }
                    if (item.search_engine === 'yandex') {
                        day.yandexScoreTotal += item.brand_score;
                        day.yandexScoreCount++;
                    }
                }
            }
        });

        const trends = Array.from(daysMap.values()).map(day => ({
            ...day,
            avgBrandScore: day.scoreCount > 0 ? Math.round(day.totalScore / day.scoreCount) : null,
            googleAvgScore: day.googleScoreCount > 0 ? Math.round(day.googleScoreTotal / day.googleScoreCount) : 0,
            yandexAvgScore: day.yandexScoreCount > 0 ? Math.round(day.yandexScoreTotal / day.yandexScoreCount) : 0
        }));

        // Распределение Brand Score
        const distribution = [
            { name: 'Отлично (80-100)', min: 80, max: 100, count: 0 },
            { name: 'Хорошо (60-79)', min: 60, max: 79, count: 0 },
            { name: 'Средне (40-59)', min: 40, max: 59, count: 0 },
            { name: 'Плохо (20-39)', min: 20, max: 39, count: 0 },
            { name: 'Критично (0-19)', min: 0, max: 19, count: 0 }
        ];

        data.forEach(item => {
            if (item.brand_score !== null) {
                const dist = distribution.find(d => item.brand_score >= d.min && item.brand_score <= d.max);
                if (dist) dist.count++;
            }
        });

        return {
            trends,
            distribution: distribution.filter(d => d.count > 0).map(d => ({ name: d.name, value: d.count }))
        };
    }, [data, dateFrom, dateTo]);

    const formatDate = (dateString) => {
        try {
            return format(parseISO(dateString), 'd MMM', { locale: ru });
        } catch {
            return dateString;
        }
    };

    const getBrandScoreColor = (score) => {
        if (score >= 80) return COLORS.success;
        if (score >= 60) return COLORS.info;
        if (score >= 40) return COLORS.warning;
        if (score >= 20) return '#f97316';
        return COLORS.danger;
    };

    // Статистика для карточек
    const statsCards = useMemo(() => {
        const total = data.length;
        const google = data.filter(d => d.search_engine === 'google').length;
        const yandex = data.filter(d => d.search_engine === 'yandex').length;
        const avgScore = total > 0
            ? Math.round(data.reduce((acc, curr) => acc + (curr.brand_score || 0), 0) / data.filter(d => d.brand_score !== null).length) || 0
            : 0;
        const siteChecked = data.filter(d => d.site_mentioned !== null && d.site_mentioned !== undefined);
        const siteFound = siteChecked.filter(d => d.site_mentioned === 1 || d.site_mentioned === true);

        return { total, google, yandex, avgScore, siteFound: siteFound.length, siteChecked: siteChecked.length };
    }, [data]);

    // Секция 1: Карточки статистики и основные графики (Тренд, Распределение)
    const renderSection1 = () => (
        <div style={{ padding: '20px', background: 'white', maxWidth: '1000px' }}>
            {/* Статистические карточки */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '25px' }}>
                <div style={{ padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '5px' }}>Всего проверок</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>{statsCards.total}</div>
                </div>
                <div style={{ padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '5px' }}>Поисковые системы</div>
                    <div style={{ display: 'flex', gap: '15px' }}>
                        <div>
                            <span style={{ fontSize: '12px', color: '#4285F4', fontWeight: 'bold' }}>G: </span>
                            <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>{statsCards.google}</span>
                        </div>
                        <div>
                            <span style={{ fontSize: '12px', color: '#FC3F1D', fontWeight: 'bold' }}>Я: </span>
                            <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>{statsCards.yandex}</span>
                        </div>
                    </div>
                </div>
                <div style={{ padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '5px' }}>Средний Brand Score</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: getBrandScoreColor(statsCards.avgScore) }}>{statsCards.avgScore}</div>
                </div>
                <div style={{ padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '5px' }}>Сайт в выдаче</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: statsCards.siteChecked > 0 ? (statsCards.siteFound > 0 ? '#10b981' : '#ef4444') : '#9ca3af' }}>
                        {statsCards.siteChecked > 0 ? `${statsCards.siteFound}/${statsCards.siteChecked}` : '—'}
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px' }}>
                    <h3 style={{ marginBottom: '15px', color: '#4a5568', fontSize: '16px' }}>Динамика проверок и Brand Score</h3>
                    <AreaChart width={450} height={250} data={chartData.trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={formatDate} hide={false} />
                        <YAxis yAxisId="left" label={{ value: 'Кол-во', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                        <Legend />
                        <Area yAxisId="left" type="monotone" dataKey="checksCount" name="Проверки" stroke={COLORS.primary} fill="#667eea30" />
                        <Area yAxisId="right" type="monotone" dataKey="avgBrandScore" name="Brand Score" stroke={COLORS.success} fill="none" />
                    </AreaChart>
                </div>

                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px' }}>
                    <h3 style={{ marginBottom: '15px', color: '#4a5568', fontSize: '16px' }}>Распределение Brand Score</h3>
                    <PieChart width={400} height={250}>
                        <Pie
                            data={chartData.distribution}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={75}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, value }) => `${value}`}
                        >
                            {chartData.distribution.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={BRAND_SCORE_COLORS[index % BRAND_SCORE_COLORS.length]} />
                            ))}
                        </Pie>
                        <Legend layout="vertical" align="right" verticalAlign="middle" />
                    </PieChart>
                </div>
            </div>
        </div>
    );

    // Секция 2: Подробное сравнение Google vs Яндекс
    const renderSection2 = () => (
        <div style={{ padding: '20px', background: 'white', maxWidth: '1000px' }}>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px' }}>
                <h3 style={{ marginBottom: '15px', color: '#4a5568', fontSize: '16px' }}>Общая видимость бренда (Score)</h3>
                <BarChart width={900} height={350} data={chartData.trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={formatDate} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} domain={[0, 100]} />
                    <Legend />
                    <Bar dataKey="googleAvgScore" name="Google Score" fill={COLORS.google} />
                    <Bar dataKey="yandexAvgScore" name="Yandex Score" fill={COLORS.yandex} />
                </BarChart>
            </div>
        </div>
    );

    return (
        <div className="report-charts-container">
            {(section === 'all' || section === 1) && renderSection1()}
            {(section === 'all' || section === 2) && renderSection2()}
        </div>
    );
};

export default ReportCharts;
